import { useState, useEffect, useRef } from "react";
import { proxyImg, api, API } from "@/lib/api";
import { track } from "@/lib/analytics";
import { ExternalLink } from "lucide-react";
import { PLACEMENT_MAP } from "@/lib/adPlacements";

/* ---------------- Ad slot (multi-placement, rotating, tracked) ---------------- */
const _adCache = {};
function fetchAds(placement) {
  if (!_adCache[placement]) {
    _adCache[placement] = api.ads(placement).then((d) => d.items || []).catch(() => []);
  }
  return _adCache[placement];
}

function trackClick(slug) {
  try {
    const url = `${API}/ads/${slug}/click`;
    if (navigator.sendBeacon) navigator.sendBeacon(url);
    else api.adClick(slug);
  } catch { /* non-blocking */ }
}

export function AdSlot({ placement, className = "" }) {
  const cfg = PLACEMENT_MAP[placement];
  const [ad, setAd] = useState(null); // null=loading, false=none
  const seen = useRef(false);

  useEffect(() => {
    let live = true;
    fetchAds(placement).then((items) => {
      if (!live) return;
      if (!items.length) return setAd(false);
      setAd(items[Math.floor(Math.random() * items.length)]);
    });
    return () => { live = false; };
  }, [placement]);

  useEffect(() => {
    if (ad && !seen.current) {
      seen.current = true;
      api.adImpression(ad.slug).catch(() => {});
    }
  }, [ad]);

  if (!ad || !cfg) return null;

  const fitClass = cfg.fit === "contain" ? "object-contain" : "object-cover";
  const innerBg = cfg.fit === "contain" ? "bg-black" : "bg-klyroo-surface";
  const isHero = cfg.id === "home-top";
  const isBanner = isHero || cfg.id === "home-mid";
  const pad = isHero ? "p-3 sm:p-5" : isBanner ? "p-3 sm:p-4" : "p-2.5 sm:p-3";
  const cta = (ad.cta || "").trim();

  return (
    <div className={`sponsored-ad relative ${className}`} data-testid={`ad-${placement}-${ad.slug}`}>
      {/* gradient cyan->violet border + subtle glow (frames the ad, never overpowers it) */}
      <div className="group rounded-[18px] p-[1.5px] bg-gradient-to-br from-klyroo-cyan/80 via-klyroo-violet/40 to-klyroo-violet/80 shadow-[0_0_0_1px_rgba(0,240,255,0.08),0_10px_44px_-14px_rgba(112,0,255,0.45)] transition-shadow duration-300 hover:shadow-[0_0_0_1px_rgba(0,240,255,0.18),0_14px_52px_-12px_rgba(112,0,255,0.6)] motion-reduce:transition-none">
        <a href={ad.link} target="_blank" rel="sponsored noopener noreferrer"
          onClick={() => { trackClick(ad.slug); track.sponsoredAdClick(ad, placement); }}
          aria-label={`Sponsored: ${ad.headline || ad.name || "advertisement"}`}
          className={`relative block rounded-[16px] bg-klyroo-bg overflow-hidden ${pad} focus:outline-none focus-visible:ring-2 focus-visible:ring-klyroo-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-klyroo-bg`}>
          {/* decorative corner dot accents — purely visual, hidden from AT */}
          <span aria-hidden="true" className="hidden sm:block pointer-events-none absolute -top-0.5 -left-0.5 w-10 h-10 opacity-40" style={{ backgroundImage: "radial-gradient(rgba(0,240,255,0.6) 1px, transparent 1.5px)", backgroundSize: "6px 6px", backgroundPosition: "0 0", maskImage: "linear-gradient(135deg, black, transparent 60%)", WebkitMaskImage: "linear-gradient(135deg, black, transparent 60%)" }} />
          <span aria-hidden="true" className="hidden sm:block pointer-events-none absolute -bottom-0.5 -right-0.5 w-10 h-10 opacity-40" style={{ backgroundImage: "radial-gradient(rgba(112,0,255,0.6) 1px, transparent 1.5px)", backgroundSize: "6px 6px", maskImage: "linear-gradient(315deg, black, transparent 60%)", WebkitMaskImage: "linear-gradient(315deg, black, transparent 60%)" }} />

          {/* header: KLYROO sponsored badge (not baked into the creative) */}
          <div className="flex items-center justify-between mb-2.5 relative z-10">
            <span data-testid={`ad-badge-${ad.slug}`} className={`inline-flex items-center gap-1.5 rounded-md bg-klyroo-cyan text-klyroo-bg font-bold uppercase tracking-[0.18em] ${isHero ? "text-[11px] px-2.5 py-1" : "text-[10px] px-2 py-0.5"}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-klyroo-bg/70" /> Sponsored
            </span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Ad</span>
          </div>

          {/* advertiser creative — untouched */}
          <div className={`relative w-full rounded-xl overflow-hidden ${innerBg}`} style={{ aspectRatio: cfg.aspect }}>
            <img src={proxyImg(ad.image)} alt={ad.image_alt || ad.headline || ad.name || "Advertisement"} loading="lazy" decoding="async"
              className={`w-full h-full ${fitClass}`} />
          </div>

          {/* optional headline + CTA from the CMS (source of truth) */}
          {(cta || (isBanner && ad.headline)) && (
            <div className={`mt-3 flex ${isBanner ? "items-center justify-between gap-3" : "flex-col gap-2"} relative z-10`}>
              {isBanner && ad.headline && <span className="text-sm text-slate-300 line-clamp-1 min-w-0">{ad.headline}</span>}
              {cta && (
                <span className={`inline-flex items-center justify-center gap-1.5 rounded-lg bg-klyroo-cyan text-klyroo-bg text-xs font-semibold px-3.5 py-2 shrink-0 transition-colors duration-200 group-hover:bg-white motion-reduce:transition-none ${isBanner ? "" : "w-full"}`}>
                  {cta} <ExternalLink size={13} />
                </span>
              )}
            </div>
          )}
        </a>
      </div>
    </div>
  );
}

// Back-compat alias.
export const AdBanner = AdSlot;


/* ---------------- Deal card ---------------- */
export function DealCard({ d }) {
  const hasPrice = d.current_price && String(d.current_price).trim();
  const href = d.affiliate_url || d.product_url;
  return (
    <div data-testid={`deal-card-${d.slug}`} className="group card-surface rounded-2xl overflow-hidden hover-raise flex flex-col">
      <div className="relative">
        <div className="h-44 overflow-hidden bg-klyroo-surface"><img src={proxyImg(d.image)} alt={d.image_alt || d.product_name} className="w-full h-full object-cover" /></div>
        {d.discount_percent > 0 && <span className="absolute top-3 left-3 text-xs font-mono font-bold px-2.5 py-1 rounded-full bg-klyroo-red text-white shadow-lg" data-testid={`deal-discount-${d.slug}`}>{d.discount_percent}% OFF</span>}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="overline text-klyroo-cyan">{d.category}</span>
        </div>
        <h3 className="font-display font-bold text-white text-base leading-snug group-hover:text-klyroo-cyan transition-colors line-clamp-2">{d.product_name}</h3>
        <div className="mt-2 flex items-center gap-2 text-xs font-mono text-slate-400">
          <span className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-slate-200">{d.retailer}</span>
          <span>{d.country}</span>
        </div>
        <div className="flex items-baseline gap-2 mt-4 flex-1">
          {hasPrice
            ? <span className="font-display text-2xl font-extrabold text-white">{d.current_price}</span>
            : <span className="text-sm text-slate-400">Price unavailable · <span className="text-slate-300">check retailer</span></span>}
          {hasPrice && d.previous_price && d.previous_price !== d.current_price && <span className="text-sm text-slate-500 line-through">{d.previous_price}</span>}
        </div>
        <a href={href} target="_blank" rel="sponsored noopener noreferrer" data-testid={`deal-get-${d.slug}`}
          onClick={() => track.dealClick(d)}
          className="mt-4 text-center px-3 py-2.5 rounded-lg bg-klyroo-cyan text-black text-sm font-semibold hover:bg-white transition-colors flex items-center justify-center gap-1.5">
          View Deal <ExternalLink size={14} />
        </a>
        {freshnessLabel(d) && <div className="mt-3 text-[11px] font-mono text-slate-500" data-testid={`deal-fresh-${d.slug}`}>Updated {freshnessLabel(d)}</div>}
      </div>
    </div>
  );
}

function freshnessLabel(d) {
  const raw = d.updated_at || d.fetched_at || d.last_checked;
  if (!raw) return null;
  const t = new Date(raw);
  if (isNaN(t)) return null;
  const mins = Math.floor((Date.now() - t.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return t.toLocaleDateString();
}
